import { useMemo, useState } from 'react';
import moment from 'moment';
import styles from './ExportOrders.module.css';
import { Button } from '../../../../../../components/Button/Button';
import { pushNotify } from '../../../../../../components/Notifications';
import { useGetCurrenciesQuery } from '../../../../../../redux/services/CurrencyApi';
import { useLazyExportOrdersQuery } from '../../../../../../redux/services/OrdersApi';

const DATE_FORMAT = 'YYYY-MM-DD';

const errorText = (error: unknown): string => {
  const data = (error as { data?: unknown })?.data;
  if (data && typeof data === 'object') {
    return Object.values(data as Record<string, unknown>).flat().join(' ');
  }
  return 'Не удалось сформировать файл, попробуйте ещё раз.';
};

export const ExportOrders = () => {
  const [dateFrom, setDateFrom] = useState(moment().startOf('month').format(DATE_FORMAT));
  const [dateTo, setDateTo] = useState(moment().format(DATE_FORMAT));
  const [paymentMethod, setPaymentMethod] = useState('');
  const [currency, setCurrency] = useState('');
  const { data: currencies } = useGetCurrenciesQuery('order');
  const [exportOrders, { isFetching }] = useLazyExportOrdersQuery();

  const methods = useMemo(
    () => Array.from(new Set((currencies || []).flatMap((item) => item.payment_methods))),
    [currencies],
  );
  const hasManyCurrencies = (currencies || []).length > 1;

  const handleExport = async () => {
    if (!dateFrom || !dateTo) {
      pushNotify('Укажите обе даты периода.', 'Выгрузка заявок');
      return;
    }
    const result = await exportOrders({
      date_from: dateFrom,
      date_to: dateTo,
      payment_method: paymentMethod || undefined,
      currency: currency || undefined,
    });
    if (result.error || typeof result.data !== 'string') {
      pushNotify(errorText(result.error), 'Выгрузка заявок');
      return;
    }
    const name = ['orders', currency, paymentMethod, dateFrom, dateTo].filter(Boolean).join('_');
    const link = document.createElement('a');
    link.href = result.data;
    link.download = `${name}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(result.data);
  };

  return (
    <div className={styles.wrapper}>
      <label className={styles.field}>
        <span>С</span>
        <input type='date' value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
      </label>
      <label className={styles.field}>
        <span>По</span>
        <input type='date' value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
      </label>
      {hasManyCurrencies && (
        <label className={styles.field}>
          <span>Валюта</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value=''>Все</option>
            {(currencies || []).map((item) => (
              <option key={item.code} value={item.code}>
                {item.code}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className={styles.field}>
        <span>Метод</span>
        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          <option value=''>Все</option>
          {methods.map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>
      </label>
      <Button
        id='export-orders'
        text={isFetching ? 'Формируем…' : 'Выгрузить в Excel'}
        action={handleExport}
        disabled={isFetching}
        filled={false}
      />
    </div>
  );
};
